<template>
	<main class="portfolio-page">
		<header id="accueil" class="hero">
			<nav class="site-nav" aria-label="Navigation principale">
				<a class="brand" href="#accueil"><span class="brand-mark">RD</span><span>René Domingue</span></a>
				<div class="nav-links"><a href="#apropos">Qui suis-je</a><a href="#contact">Contact</a><router-link
						:to="isAuthenticated ? '/tattoo' : '/login'">Espace privé</router-link></div>
			</nav>
			<div class="orb" aria-hidden="true"></div>
			<div class="hero-content">
				<p class="eyebrow">Développeur front-end - Concepteur UI/UX accessibility</p>
				<h1>Je crée des expériences web <em>propres, efficaces</em> et utiles.</h1>
				<p class="hero-copy">Je suis René Domingue, développeur front-end à Montréal. Je transforme les idées en
					interfaces accessibles, soignées et faciles à utiliser.</p>
				<div class="hero-actions"><a class="button" href="#apropos">Me découvrir <span>↘</span></a><a
						class="text-link" href="#contact">Me contacter</a></div>
			</div>
			<p class="side-label">01 — Accueil</p>
		</header>

		<section id="apropos" class="about-section">
			<div class="about-intro">
				<p class="eyebrow">Qui suis-je</p>
				<h2>Plus de 15 ans à donner vie aux idées sur le web.</h2>
			</div>
			<div class="about-content">
				<figure class="portrait"><img :src="portraitImage" alt="René Domingue" />
					<figcaption>Montréal<br>Québec</figcaption>
				</figure>
				<div class="about-copy">
					<h3>Bonjour, je suis René.</h3>
					<p>Je suis développeur front-end à Montréal et disponible pour des contrats à la pige. J’aime bâtir
						des produits numériques où le design, la technologie et l’expérience utilisateur travaillent
						ensemble.</p>
					<p>Mon bagage couvre autant l’intégration que le développement d’interfaces modernes, avec une
						attention particulière à l’accessibilité, au référencement et à la performance.</p><a
						class="text-link" href="mailto:rdomingue80@gmail.com">Me contacter ↗</a>
				</div>
			</div>
		</section>

		<section class="skills-section">
			<p class="eyebrow">Expertise</p>
			<h2>Outils & savoir-faire</h2>
			<ul>
				<li><span>01</span><strong>HTML / CSS</strong><small>Intégration précise et accessible</small></li>
				<li><span>02</span><strong>JavaScript</strong><small>Interactions fluides et robustes</small></li>
				<li><span>03</span><strong>Vue.js</strong><small>Interfaces modernes et évolutives</small></li>
				<li><span>04</span><strong>Design UI</strong><small>Photoshop, Figma et prototypage</small></li>
				<li><span>05</span><strong>Performance</strong><small>SEO, accessibilité et optimisation</small></li>
			</ul>
		</section>

		<footer id="contact">
			<p class="eyebrow">Un projet en tête?</p>
			<div class="footer-main">
				<h2>Créons quelque chose d’utile.</h2><a href="mailto:rdomingue80@gmail.com">Parlons-en ↗</a>
			</div>
			<div class="footer-bottom"><span>René Domingue</span><span>Développeur front-end · Montréal</span>
			</div>
		</footer>
	</main>
</template>

<script setup>
import { computed } from 'vue'
import { useStore } from 'vuex'
import portraitImage from '@/assets/IMG_1286.jpg'
const store = useStore()
const isAuthenticated = computed(() => store.getters.isAuthenticated)
</script>

<style scoped>
figure.portrait {
	display: block;
	margin: 0;
	color: #fff
}

figure.portrait:after {
	inset: 0;
	width: auto;
	aspect-ratio: auto;
	border-radius: 0;
	right: 0;
	bottom: 0;
	background: linear-gradient(180deg, transparent 55%, rgba(0, 0, 0, .7))
}

figure.portrait img {
	width: 100%;
	height: 100%;
	object-fit: cover;
	object-position: center 28%;
	filter: grayscale(100%);
	transition: filter .35s ease, transform .5s ease
}

figure.portrait:hover img {
	filter: grayscale(0);
	transform: scale(1.025)
}

figure.portrait figcaption {
	position: absolute;
	z-index: 2;
	right: 22px;
	bottom: 22px;
	color: #fff;
	font-size: .6rem;
	font-weight: 900;
	line-height: 1.5;
	letter-spacing: .15em;
	text-align: right;
	text-transform: uppercase
}

.portfolio-page {
	--ink: #121212;
	--paper: #f3f0e9;
	--lime: #d8ff3e;
	--orange: #ff6846;
	width: 100vw;
	margin-left: calc(50% - 50vw);
	background: var(--paper);
	color: var(--ink);
	overflow: hidden
}

.hero {
	position: relative;
	min-height: 100svh;
	display: flex;
	align-items: center;
	background: var(--ink);
	color: #fff;
	overflow: hidden
}

.site-nav {
	position: absolute;
	z-index: 5;
	inset: 0 0 auto;
	display: flex;
	align-items: center;
	justify-content: space-between;
	width: min(1320px, calc(100% - 64px));
	margin: auto;
	padding: 27px 0;
	border-bottom: 1px solid #3a3a3a
}

.brand {
	display: flex;
	align-items: center;
	gap: 12px;
	color: #fff;
	font-weight: 700;
	letter-spacing: .04em
}

.brand-mark {
	display: grid;
	place-items: center;
	width: 42px;
	height: 42px;
	border-radius: 50%;
	background: var(--lime);
	color: var(--ink);
	font-size: .75rem;
	font-weight: 900
}

.nav-links {
	display: flex;
	gap: 32px
}

.nav-links a {
	color: #bbb;
	font-size: .7rem;
	font-weight: 700;
	letter-spacing: .13em;
	text-transform: uppercase
}

.nav-links a:hover {
	color: var(--lime)
}

.hero-content {
	position: relative;
	z-index: 2;
	width: min(1320px, calc(100% - 64px));
	margin: auto;
	padding: 150px 0 80px
}

.eyebrow {
	margin: 0 0 20px;
	color: #777;
	font-size: .68rem;
	font-weight: 800;
	letter-spacing: .22em;
	text-transform: uppercase
}

.hero .eyebrow {
	color: var(--lime)
}

.hero h1 {
	max-width: 1050px;
	margin: 0;
	font-size: clamp(3.3rem, 7.4vw, 7.4rem);
	font-weight: 500;
	line-height: .93;
	letter-spacing: -.065em
}

.hero h1 em {
	color: var(--lime);
	font-family: Georgia, serif;
	font-weight: 400
}

.hero-copy {
	max-width: 650px;
	margin: 36px 0;
	color: #aaa;
	font-size: clamp(1rem, 1.5vw, 1.22rem);
	line-height: 1.7
}

.hero-actions {
	display: flex;
	align-items: center;
	gap: 28px
}

.button {
	display: inline-flex;
	gap: 32px;
	padding: 17px 21px;
	background: var(--lime);
	color: var(--ink);
	font-size: .72rem;
	font-weight: 900;
	letter-spacing: .1em;
	text-transform: uppercase
}

.button:hover {
	transform: translateY(-3px);
	background: #fff
}

.text-link {
	padding-bottom: 5px;
	border-bottom: 1px solid #666;
	color: #fff;
	font-size: .72rem;
	font-weight: 800;
	letter-spacing: .1em;
	text-transform: uppercase
}

.orb {
	position: absolute;
	right: -10vw;
	top: 15%;
	width: 46vw;
	height: 46vw;
	border-radius: 50%;
	border: 1px solid #353535;
	box-shadow: 0 0 0 6vw #171717, 0 0 0 calc(6vw + 1px) #292929
}

.side-label {
	position: absolute;
	right: 32px;
	bottom: 32px;
	color: #777;
	font-size: .62rem;
	letter-spacing: .16em;
	text-transform: uppercase;
	writing-mode: vertical-rl
}

.section,
.about-section,
.skills-section {
	padding: 125px max(32px, calc((100vw - 1320px)/2))
}

.section-heading {
	display: grid;
	grid-template-columns: 1.2fr .8fr;
	gap: 60px;
	align-items: end;
	margin-bottom: 65px
}

.section-heading h2,
.about-intro h2,
.skills-section h2,
footer h2 {
	margin: 0;
	font-size: clamp(3.3rem, 7vw, 7rem);
	font-weight: 500;
	line-height: .92;
	letter-spacing: -.065em
}

.section-heading>p {
	max-width: 500px;
	margin: 0;
	color: #666;
	line-height: 1.75
}

.work-grid {
	display: grid;
	grid-template-columns: 1.15fr .85fr;
	gap: 42px
}

.featured {
	grid-row: span 2
}

.project-visual {
	position: relative;
	display: flex;
	align-items: center;
	min-height: 320px;
	padding: 40px;
	overflow: hidden
}

.featured .project-visual {
	min-height: 750px
}

.project-visual span {
	z-index: 1;
	font-size: clamp(2.2rem, 5vw, 5rem);
	font-weight: 700;
	line-height: .93;
	letter-spacing: -.06em
}

.project-visual b {
	position: absolute;
	right: 24px;
	top: 20px;
	font-size: .65rem
}

.visual-one {
	background: var(--lime)
}

.visual-one:after {
	content: "";
	position: absolute;
	width: 60%;
	aspect-ratio: 1;
	right: -16%;
	bottom: 5%;
	border-radius: 50%;
	background: var(--orange);
	box-shadow: -9vw -10vw 0 #101010
}

.visual-two {
	background: #252525;
	color: #fff
}

.visual-two:after {
	content: "";
	position: absolute;
	right: -10%;
	width: 60%;
	aspect-ratio: 1;
	border: 2px solid var(--lime);
	transform: rotate(25deg)
}

.visual-three {
	background: var(--orange)
}

.visual-three:after {
	content: "";
	position: absolute;
	width: 55%;
	height: 140%;
	right: 5%;
	background: var(--paper);
	transform: rotate(18deg)
}

.project-meta {
	display: flex;
	justify-content: space-between;
	align-items: center;
	padding: 22px 0;
	border-bottom: 1px solid #bbb
}

.project-meta small {
	color: #777;
	text-transform: uppercase;
	letter-spacing: .1em
}

.project-meta h3 {
	margin: 4px 0 0;
	font-size: 1.15rem
}

.project-meta i {
	font-style: normal;
	font-size: 1.5rem
}

.about-section {
	background: #171717;
	color: #fff
}

.about-intro {
	max-width: 1000px;
	margin-bottom: 80px
}

.about-intro h2 {
	font-size: clamp(3rem, 6vw, 6rem)
}

.about-content {
	display: grid;
	grid-template-columns: .8fr 1.2fr;
	gap: 8vw;
	align-items: center
}

.portrait {
	position: relative;
	display: flex;
	align-items: center;
	justify-content: center;
	aspect-ratio: 4/5;
	background: var(--lime);
	color: #121212;
	overflow: hidden
}

.portrait:after {
	content: "";
	position: absolute;
	width: 90%;
	aspect-ratio: 1;
	border-radius: 50%;
	background: var(--orange);
	right: -45%;
	bottom: -18%
}

.portrait span {
	font-family: Georgia, serif;
	font-size: clamp(10rem, 25vw, 22rem);
	font-style: italic
}

.portrait small {
	position: absolute;
	z-index: 1;
	right: 22px;
	bottom: 22px;
	font-size: .6rem;
	font-weight: 900;
	letter-spacing: .15em;
	text-transform: uppercase
}

.about-copy {
	max-width: 650px
}

.about-copy h3 {
	margin: 0 0 28px;
	font-size: clamp(2rem, 4vw, 3.5rem)
}

.about-copy p {
	color: #aaa;
	font-size: 1.05rem;
	line-height: 1.85
}

.about-copy .text-link {
	display: inline-block;
	margin-top: 22px
}

.skills-section h2 {
	margin-bottom: 65px;
	font-size: clamp(3rem, 6vw, 6rem)
}

.skills-section ul {
	margin: 0;
	padding: 0;
	border-top: 1px solid #bbb;
	list-style: none
}

.skills-section li {
	display: grid;
	grid-template-columns: 70px 1fr 1fr;
	gap: 20px;
	align-items: center;
	padding: 25px 0;
	border-bottom: 1px solid #bbb
}

.skills-section li span,
.skills-section li small {
	color: #777
}

.skills-section li strong {
	font-size: clamp(1.25rem, 2.5vw, 2rem);
	font-weight: 500
}

footer {
	padding: 115px max(32px, calc((100vw - 1320px)/2)) 35px;
	background: var(--lime)
}

footer .eyebrow {
	color: #4c5917
}

.footer-main {
	display: flex;
	align-items: flex-end;
	justify-content: space-between;
	gap: 50px
}

.footer-main h2 {
	max-width: 900px
}

.footer-main a {
	flex: none;
	padding-bottom: 8px;
	border-bottom: 2px solid #111;
	color: #111;
	font-size: .8rem;
	font-weight: 900;
	text-transform: uppercase
}

.footer-bottom {
	display: flex;
	justify-content: space-between;
	margin-top: 100px;
	padding-top: 22px;
	border-top: 1px solid #758721;
	font-size: .65rem;
	font-weight: 700;
	letter-spacing: .1em;
	text-transform: uppercase
}

@media(max-width:800px) {
	.site-nav {
		width: calc(100% - 32px)
	}

	.nav-links {
		gap: 14px
	}

	.nav-links a {
		font-size: .56rem
	}

	.nav-links a:nth-child(2) {
		display: none
	}

	.hero-content {
		width: calc(100% - 32px)
	}

	.hero h1 {
		font-size: clamp(3.3rem, 15vw, 5.7rem)
	}

	.orb {
		width: 75vw;
		height: 75vw;
		right: -40vw
	}

	.section,
	.about-section,
	.skills-section {
		padding: 85px 16px
	}

	.section-heading,
	.about-content {
		grid-template-columns: 1fr
	}

	.work-grid {
		grid-template-columns: 1fr
	}

	.featured {
		grid-row: auto
	}

	.featured .project-visual,
	.project-visual {
		min-height: 430px
	}

	.about-content {
		gap: 55px
	}

	.skills-section li {
		grid-template-columns: 40px 1fr
	}

	.skills-section li small {
		grid-column: 2
	}

	.footer-main,
	.footer-bottom {
		align-items: flex-start;
		flex-direction: column;
		gap: 25px
	}

	.footer-bottom {
		gap: 10px;
		margin-top: 70px
	}
}
</style>
